import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import STORAGE_KEYS from '@/configs/local-storage-keys';
import {
  CHART_CALCULATION,
  CHART_FIELD,
  CHART_FIELD_ITEM_SELECTION_METHOD,
  CHART_GRAPH,
  CHART_GRAPH_DIRECTION,
  CHART_METRIC,
  CHART_METRIC_VIEW_TYPE,
  CHART_TYPE,
} from '@/entities/chart/model/constants';
import { convertSignalDatePresetToChartTerm } from '@/features/(signal)/signals/lib';
import type { CompositedSignalIssue } from '@/features/(signal)/signals/model';
import {
  type ChartStateStorage,
  chartStateStorageSchema,
  type SignalDateSelectionInput,
} from '@/features/(signal)/signals/model';
import useStates from '@/hooks/pure/useStates';
import useWorkspaceStorage from '@/hooks/pure/useWorkspaceStorage';
import service from '@/services';
import { createStorageParser } from '@/utils/storage/createStorageParser';

type Props = {
  filterSet: StructuredFilter;
  issue: CompositedSignalIssue;
  datePreset: SignalDateSelectionInput;
};

type GetInitialChartState = (props: {
  datePreset: SignalDateSelectionInput;
  filterSet: StructuredFilter;
}) => ChartState;

const getInitialChartState: GetInitialChartState = ({
  datePreset,
  filterSet,
}) => {
  const term = convertSignalDatePresetToChartTerm(datePreset);

  return {
    type: CHART_TYPE.SINGLE_AXIS,
    historical: {
      frequency: null,
      value: false,
    },
    graph_direction: CHART_GRAPH_DIRECTION.VERTICAL,
    graph: CHART_GRAPH.BAR,
    field: {
      type: CHART_FIELD.FEEDBACK_TAG,
      entire: true,
      candidates: [],
    },
    selected_field: {
      method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_5,
      items: [],
    },
    target: {
      filter: filterSet,
      is_compare: false,
      term: term,
    },
    series: [
      {
        measure: {
          metric: CHART_METRIC.FEEDBACK,
          calculation: CHART_CALCULATION.COUNT,
        },
        options: {
          view_type: CHART_METRIC_VIEW_TYPE.COUNT,
        },
      },
    ],
  };
};

const getIsDatePresetObjectDiff = (prev: ChartTermDef, next: ChartTermDef) => {
  if (!next || !prev) return true;
  for (const [key, value] of Object.entries(prev)) {
    if (value !== next[key as keyof typeof next]) return true;
  }
  return false;
};

const useCustomChartState = (props: Props): ChartContextProps => {
  // filter에 이미 UTC로 변환된 start_date/end_date가 포함되어 있음
  // 날짜 필터를 제거하지 않고 그대로 전달하여 일관된 기간 데이터 조회

  // localStorage에서 저장된 chart config 복원
  const [storedConfig, setStoredConfig] =
    useWorkspaceStorage<ChartStateStorage | null>(
      STORAGE_KEYS.SIGNAL_CUSTOM_CHART_STATE,
      null,
      { parser: createStorageParser(chartStateStorageSchema, null) },
    );

  const [selectedField, setSelectedField] = useState<ChartSelectedFieldItemDef>(
    {
      method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_5,
      items: [],
    },
  );
  const [tempMetaValues, setTempMetaValues] = useState<
    ChartFieldItemMeta[] | null
  >(null);

  // localStorage 값이 있으면 초기 state에 merge (target은 항상 props에서 생성)
  const baseState = getInitialChartState({
    datePreset: props.datePreset,
    filterSet: props.filterSet,
  });
  const initialState = storedConfig
    ? { ...baseState, ...storedConfig, target: baseState.target }
    : baseState;

  const {
    state,
    setState: _setState,
    setValue: _setValue,
    initialize: _initialize,
  } = useStates<ChartState>(initialState);

  // localStorage 저장 헬퍼
  const persistState = (nextState: ChartState) => {
    const { target: _, ...configToStore } = nextState;
    setStoredConfig(configToStore as ChartStateStorage);
  };

  // setState 래핑: state 변경 시 localStorage에 즉시 저장
  const setState: typeof _setState = (newState) => {
    _setState(newState);
    const partial = typeof newState === 'function' ? newState(state) : newState;
    persistState({ ...state, ...partial });
  };

  // setValue 래핑: 개별 값 변경 시 localStorage에 즉시 저장
  const setValue: typeof _setValue = (key, value) => {
    _setValue(key, value);
    persistState({ ...state, [key]: value });
  };

  // initialize 래핑: 초기화 시 localStorage도 클리어
  const initialize = () => {
    _initialize();
    setStoredConfig(null);
  };

  // datePreset 변경 시 state.target.term 동기화 (렌더 중 state 조정 패턴)
  const updatedDatePreset = convertSignalDatePresetToChartTerm(
    props.datePreset,
  );
  const currentDatePreset = state.target.term;
  if (getIsDatePresetObjectDiff(currentDatePreset, updatedDatePreset)) {
    setState((prev) => ({
      target: {
        ...prev.target,
        term: convertSignalDatePresetToChartTerm(props.datePreset),
      },
    }));
  }

  const chart: ChartBase & { name: string } = {
    name: 'Custom Chart',
    ...state,
    target: {
      ...state.target,
      filter: props.filterSet,
      term: convertSignalDatePresetToChartTerm(props.datePreset),
    },
  };

  const tableQuery = useQuery({
    queryKey: ['custom-chart-table', JSON.stringify(chart)],
    queryFn: () => service.chart.fetchTable(chart),
    retry: false,
  });

  return {
    mode: 'CREATE',
    state,
    setState,
    setValue,
    initialize,
    initialized: true,
    tableQuery,
    originalChartInfo: null,
    setOriginalChartInfo: () => {},
    title: 'Custom Chart',
    setTitle: () => {},
    selectedField,
    setSelectedField,
    tempMetaValues,
    setTempMetaValues,
  };
};

export default useCustomChartState;
